import * as XLSX from 'xlsx-js-style';

const safeName = (text) => String(text || 'encuesta').replace(/[^a-zA-Z0-9]/g, '_');

const border = (rgb) => ({
  top: { style: 'thin', color: { rgb } },
  bottom: { style: 'thin', color: { rgb } },
  left: { style: 'thin', color: { rgb } },
  right: { style: 'thin', color: { rgb } }
});

const headerStyle = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
  fill: { fgColor: { rgb: '7C3AED' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: border('5B21B6')
};

const titleStyle = {
  font: { bold: true, color: { rgb: '7C3AED' }, sz: 14 },
  alignment: { horizontal: 'left', vertical: 'center' }
};

const subtitleStyle = {
  font: { bold: true, color: { rgb: '555555' }, sz: 11 },
  alignment: { horizontal: 'left', vertical: 'center' }
};

const rowStyle = (isEven) => ({
  font: { sz: 11, color: { rgb: '333333' } },
  fill: { fgColor: { rgb: isEven ? 'F3EDFF' : 'FFFFFF' } },
  alignment: { vertical: 'center', wrapText: true },
  border: border('D9D9D9')
});

const numStyle = (isEven) => ({
  ...rowStyle(isEven),
  alignment: { horizontal: 'center', vertical: 'center' }
});

export const downloadResponsesExcel = ({ responses, surveyTitle, t }) => {
  const workbook = XLSX.utils.book_new();
  const title = surveyTitle || t('dash_excel_question');
  const maxResponses = responses.reduce((acc, q) => Math.max(acc, q.respuestas.length), 0);

  const summaryData = [
    [{ v: title, s: { font: { bold: true, color: { rgb: '7C3AED' }, sz: 18 }, alignment: { horizontal: 'center' } } }, ''],
    [{ v: `${t('dash_excel_num_responses')}: ${maxResponses}`, s: { font: { sz: 11, color: { rgb: '888888' } }, alignment: { horizontal: 'center' } } }, ''],
    ['', ''],
    [{ v: '#', s: headerStyle }, { v: t('dash_excel_question'), s: headerStyle }]
  ];
  responses.forEach((question, i) => {
    summaryData.push([{ v: i + 1, s: numStyle(i % 2 === 0) }, { v: question.pregunta, s: rowStyle(i % 2 === 0) }]);
  });
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [{ width: 8 }, { width: 90 }];
  summarySheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }
  ];
  XLSX.utils.book_append_sheet(workbook, summarySheet, t('dash_excel_summary') || 'Resumen');

  responses.forEach((question, questionIndex) => {
    const sheetData = [
      [{ v: question.pregunta, s: titleStyle }, ''],
      [{ v: `${t('dash_excel_num_responses')}: ${question.respuestas.length}`, s: subtitleStyle }, ''],
      ['', ''],
      [{ v: '#', s: headerStyle }, { v: t('dash_excel_response'), s: headerStyle }]
    ];
    question.respuestas.forEach((answer, i) => {
      sheetData.push([{ v: i + 1, s: numStyle(i % 2 === 0) }, { v: answer.respuesta, s: rowStyle(i % 2 === 0) }]);
    });
    const sheet = XLSX.utils.aoa_to_sheet(sheetData);
    sheet['!cols'] = [{ width: 8 }, { width: 90 }];
    sheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }
    ];
    XLSX.utils.book_append_sheet(workbook, sheet, `${t('dash_excel_question')} ${questionIndex + 1}`.substring(0, 31));
  });

  XLSX.writeFile(workbook, `respuestas_${safeName(surveyTitle)}.xlsx`);
};

const requestError = async (response, step) => {
  const text = await response.text().catch(() => '');
  const error = new Error(`${step} ${response.status} ${text.slice(0, 200)}`);
  error.status = response.status;
  error.step = step;
  return error;
};

export const downloadSurveyPdf = async (survey) => {
  const token = localStorage.getItem('token');
  const guardado = localStorage.getItem('thepulse_language');
  const lang = ['en', 'zh'].includes(guardado) ? guardado : 'es';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const chartsResponse = await fetch(`${process.env.REACT_APP_API_URL}/enviar-id-encuesta`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ id_encuesta: survey.id_encuesta, max_words: 20, conexiones_max_words: 10, lang })
  });
  if (!chartsResponse.ok) throw await requestError(chartsResponse, 'charts');
  const charts = await chartsResponse.json();

  const pdfResponse = await fetch(`${process.env.REACT_APP_AI_URL}/api/generar_pdf`, {
    method: 'POST',
    headers,
    // El informe se monta en el servidor con los mismos datos que la página de resultados
    body: JSON.stringify({
      id_encuesta: survey.id_encuesta,
      lang,
      resultados: {
        conclusion: charts.conclusion || '',
        conclusion_estructurada: charts.conclusion_estructurada || null,
        chartData: charts.chartData || {},
        analisis_individual: charts.analisis_individual || []
      }
    })
  });
  if (!pdfResponse.ok) throw await requestError(pdfResponse, 'pdf');

  const blob = await pdfResponse.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `informe_${safeName(survey.titulo)}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};
